import React, { useEffect, useContext } from 'react';
import Router from 'next/router';
import NProgress from 'nprogress';
import Head from 'next/head';
import { CookieBanner } from 'day8-react/build/content';
import { Navbar, Footer, NotificationProvider } from 'day8-react/build/new-components';
import { GlobalContext, GlobalProvider } from '../context/GlobalState';
import { WpPageType } from '../globalTypes';
import * as gtag from '../helpers/gtag';

const DEFAULT_TITLE = 'The Ski Week';
const DEFAULT_DESCRIPTION = 'A series of week-long and weekend boutique ski festivals staged in handpicked mountain towns across the world.';
const DEFAULT_IMAGE = 'https://www.theskiweek.com/wp-content/uploads/2017/01/darkerbg.jpg';

type Layout = {
  page: WpPageType
  children: React.ReactNode
  hiddenCurrency?: boolean
  signupHidden?: boolean
  className?: string
  isFooterHidden?: boolean
  isNavbarHidden?: boolean
  isCookieBannerHidden?: boolean
  canonical?: string
}

const Layout: React.FC<Layout> = ({
  page,
  children,
  hiddenCurrency,
  signupHidden,
  className,
  isFooterHidden,
  isNavbarHidden,
  isCookieBannerHidden,
  canonical
}) => {
  useEffect(() => {
    const handleRouteStart = () => NProgress.start();
    const handleRouteDone = () => {
      NProgress.done();
      gtag.pageview(Router.asPath);
    };
    Router.events.on('routeChangeStart', handleRouteStart);
    Router.events.on('routeChangeComplete', handleRouteDone);
    Router.events.on('routeChangeError', handleRouteDone);
    return () => {
      // Make sure to remove the event handler on unmount!
      Router.events.off('routeChangeStart', handleRouteStart);
      Router.events.off('routeChangeComplete', handleRouteDone);
      Router.events.off('routeChangeError', handleRouteDone);
    };
  }, []);
  const globalContext = useContext(GlobalContext);
  const { toggleShowCookieBanner, showCookieBanner } = globalContext;
  const { yoast } = page;
  const title = (yoast && yoast.title) || page.seo_title || (page.title && page.title.rendered) || DEFAULT_TITLE;
  const ogDescription = yoast ? yoast.opengraph_description : null;
  const description = (yoast && yoast.metadesc) || page.seo_description || DEFAULT_DESCRIPTION;
  let image = DEFAULT_IMAGE;
  if (yoast && yoast.opengraph_image) {
    image = yoast.opengraph_image;
  } else if (page.featured_image_src && !page.featured_image_src.includes('media/default.png')) {
    image = page.featured_image_src;
  }
  let currencyHidden = false;
  if (page.acf && page.acf.hide_currency) {
    currencyHidden = page.acf.hide_currency;
  } else if (hiddenCurrency) {
    currencyHidden = hiddenCurrency;
  }
  return (
    <NotificationProvider>
      <GlobalProvider>
        <Head>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta key="og:type" property="og:type" content="website" />
          <meta key="og:image" property="og:image" content={image} />
          <meta key="og:title" property="og:title" content={title} />
          <meta key="og:description" property="og:description" content={ogDescription || description} />
          <meta name="theme-color" content="#182c4b" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
          {canonical && <link rel="canonical" href={canonical} />}
        </Head>
        {!isNavbarHidden && <Navbar currencyHidden={currencyHidden} />}
        <div className={className}>
          {children}
          {!isFooterHidden && (
            <Footer page={page} currencyHidden signupHidden={signupHidden} />
          )}
        </div>
      </GlobalProvider>
    </NotificationProvider>
  );
};

export default Layout;

